/**
 * useOutreachFiltersParam — URL-owned state for the Inbox's own search box,
 * sort and FilterPanel (Task 25). Mirrors useOutreachSubParam's shape: one
 * hook owning every field this one feature area needs.
 *
 * Deliberately separate from the shared `sort_by`/`sort_dir` fields — see
 * search.ts's own comment on `outreach_sort` for why.
 */
import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { CampaignSearch } from '../../search';
import type { OutreachRowsParams } from '@discovr/features';
import type { OutreachFilterState } from './FilterPanel';
import { parseStageParam } from './stageParam';

const DEFAULT_SORT: NonNullable<OutreachRowsParams['sort']> = 'last_activity';

function filtersFromSearch(search: Partial<CampaignSearch>, stage: string | undefined): OutreachFilterState {
  return {
    reachedFrom: search.reached_from,
    reachedTo: search.reached_to,
    stage,
    intent: search.intent,
    lockedRate: search.locked_rate,
  };
}

export function useOutreachFiltersParam() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const parsedStage = parseStageParam(search.stage);

  const setQuery = useCallback(
    (next: string) =>
      navigate({
        to: '.',
        search: ((p: Partial<CampaignSearch>) => ({ ...p, outreach_q: next || undefined, page: undefined })),
        resetScroll: false,
      } as never),
    [navigate],
  );
  const setSort = useCallback(
    (next: NonNullable<OutreachRowsParams['sort']>) =>
      navigate({
        to: '.',
        search: ((p: Partial<CampaignSearch>) => ({ ...p, outreach_sort: next, page: undefined })),
        resetScroll: false,
      } as never),
    [navigate],
  );
  const setFilters = useCallback(
    (next: OutreachFilterState) =>
      navigate({
        to: '.',
        search: ((p: Partial<CampaignSearch>) => ({
          ...p,
          reached_from: next.reachedFrom,
          reached_to: next.reachedTo,
          stage: next.stage,
          intent: next.intent,
          locked_rate: next.lockedRate,
          page: undefined,
        })),
        resetScroll: false,
      } as never),
    [navigate],
  );

  return {
    query: search.outreach_q ?? '',
    setQuery,
    sort: search.outreach_sort ?? DEFAULT_SORT,
    setSort,
    filters: filtersFromSearch(search, parsedStage.stage),
    unrecognisedStage: parsedStage.unrecognised,
    setFilters,
  };
}
